import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  SandDollarAwardsFilters,
  StudentsFilters,
  awardCategoriesApi,
  celebrationsApi,
  housesApi,
  sandDollarAwardsApi,
  sandDollarStatsApi,
  schoolYearsApi,
  studentsApi,
} from '@/services';

export const sandDollarsKeys = {
  houses: ['admin', 'sand-dollars', 'houses'] as const,
  categories: ['admin', 'sand-dollars', 'categories'] as const,
  schoolYears: ['admin', 'sand-dollars', 'school-years'] as const,
  studentsList: (filters: StudentsFilters) => ['admin', 'sand-dollars', 'students', filters] as const,
  studentsAll: ['admin', 'sand-dollars', 'students-all'] as const,
  studentAudit: ['admin', 'sand-dollars', 'students', 'audit'] as const,
  student: (id: string) => ['admin', 'sand-dollars', 'students', 'detail', id] as const,
  awards: (filters: SandDollarAwardsFilters) => ['admin', 'sand-dollars', 'awards', filters] as const,
  celebrations: ['admin', 'sand-dollars', 'celebrations'] as const,
  celebration: (id: string) => ['admin', 'sand-dollars', 'celebrations', 'detail', id] as const,
  standings: (id: string) => ['admin', 'sand-dollars', 'celebrations', 'standings', id] as const,
  overview: (window: { from?: string; to?: string } = {}) =>
    ['admin', 'sand-dollars', 'stats', 'overview', window] as const,
  houseStats: (window: { from?: string; to?: string } = {}) =>
    ['admin', 'sand-dollars', 'stats', 'houses', window] as const,
  leaderboard: (period: string, scope: string, limit: number | 'all', window: { from?: string; to?: string } = {}) =>
    ['admin', 'sand-dollars', 'stats', 'leaderboard', { period, scope, limit, ...window }] as const,
  studentStats: (id: string) => ['admin', 'sand-dollars', 'stats', 'students', id] as const,
  teacherStats: (id: string) => ['admin', 'sand-dollars', 'stats', 'teachers', id] as const,
  classList: (window: { from?: string; to?: string } = {}) =>
    ['admin', 'sand-dollars', 'stats', 'class-list', window] as const,
};

const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['admin', 'sand-dollars'] });
};

// ── Houses ──────────────────────────────────────────────────────────────────

export function useHousesQuery() {
  return useQuery({
    queryKey: sandDollarsKeys.houses,
    queryFn: () => housesApi.list(),
  });
}

export function useCreateHouseMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof housesApi.create>[0]) => housesApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateHouseMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof housesApi.update>[1] }) =>
      housesApi.update(id, data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteHouseMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => housesApi.delete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Categories ──────────────────────────────────────────────────────────────

export function useAwardCategoriesQuery() {
  return useQuery({
    queryKey: sandDollarsKeys.categories,
    queryFn: () => awardCategoriesApi.list(),
  });
}

export function useSchoolYearsQuery() {
  return useQuery({
    queryKey: sandDollarsKeys.schoolYears,
    queryFn: () => schoolYearsApi.list(),
  });
}

export function useCreateSchoolYearMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof schoolYearsApi.create>[0]) => schoolYearsApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateSchoolYearMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof schoolYearsApi.update>[1] }) =>
      schoolYearsApi.update(id, data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof awardCategoriesApi.create>[0]) => awardCategoriesApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof awardCategoriesApi.update>[1] }) =>
      awardCategoriesApi.update(id, data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => awardCategoriesApi.delete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Students ────────────────────────────────────────────────────────────────

export function useStudentsListQuery(filters: StudentsFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sandDollarsKeys.studentsList(filters),
    queryFn: () => studentsApi.list(filters),
    ...options,
  });
}

export function useStudentsAllQuery() {
  return useQuery({
    queryKey: sandDollarsKeys.studentsAll,
    queryFn: () => studentsApi.listAll({ active: true }),
  });
}

export function useStudentAuditQuery(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: sandDollarsKeys.studentAudit,
    queryFn: () => studentsApi.audit(),
    ...options,
  });
}

export function useStudentQuery(id?: string) {
  return useQuery({
    queryKey: sandDollarsKeys.student(id ?? ''),
    queryFn: () => studentsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof studentsApi.create>[0]) => studentsApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof studentsApi.update>[1] }) =>
      studentsApi.update(id, data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteStudentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => studentsApi.delete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUploadStudentPhotoMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => studentsApi.uploadPhoto(id, file),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSetStudentGoogleEmailMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) => studentsApi.setGoogleEmail(id, email),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useClearStudentGoogleEmailMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => studentsApi.clearGoogleEmail(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Awards ──────────────────────────────────────────────────────────────────

export function useAwardsQuery(filters: SandDollarAwardsFilters = {}, enabled = true) {
  return useQuery({
    queryKey: sandDollarsKeys.awards(filters),
    queryFn: () => sandDollarAwardsApi.list(filters),
    enabled,
  });
}

export function useCreateAwardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof sandDollarAwardsApi.create>[0]) => sandDollarAwardsApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCreateAwardBatchMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (awards: Parameters<typeof sandDollarAwardsApi.createBatch>[0]) =>
      sandDollarAwardsApi.createBatch(awards),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteAwardMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sandDollarAwardsApi.delete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Celebrations ────────────────────────────────────────────────────────────

export function useCelebrationsQuery() {
  return useQuery({
    queryKey: sandDollarsKeys.celebrations,
    queryFn: () => celebrationsApi.list(),
  });
}

export function useCelebrationQuery(id?: string) {
  return useQuery({
    queryKey: sandDollarsKeys.celebration(id ?? ''),
    queryFn: () => celebrationsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useStandingsQuery(id?: string) {
  return useQuery({
    queryKey: sandDollarsKeys.standings(id ?? ''),
    queryFn: () => celebrationsApi.getStandings(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateCelebrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof celebrationsApi.create>[0]) => celebrationsApi.create(data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateCelebrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof celebrationsApi.update>[1] }) =>
      celebrationsApi.update(id, data),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteCelebrationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => celebrationsApi.delete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

// ── Stats ───────────────────────────────────────────────────────────────────

export function useOverviewQuery(window: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: sandDollarsKeys.overview(window),
    queryFn: () => sandDollarStatsApi.overview(window),
  });
}

export function useHouseStatsQuery(window: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: sandDollarsKeys.houseStats(window),
    queryFn: () => sandDollarStatsApi.houseStats(window),
  });
}

export function useLeaderboardQuery(
  period: 'week' | 'month' | 'year' | 'all' = 'month',
  scope: 'overall' | 'house' | 'grade' = 'overall',
  limit: number | 'all' = 10,
  window: { from?: string; to?: string } = {}
) {
  return useQuery({
    queryKey: sandDollarsKeys.leaderboard(period, scope, limit, window),
    queryFn: () => sandDollarStatsApi.leaderboard(period, scope, limit, window),
  });
}

export function useStudentStatsQuery(id?: string) {
  return useQuery({
    queryKey: sandDollarsKeys.studentStats(id ?? ''),
    queryFn: () => sandDollarStatsApi.studentStats(id as string),
    enabled: Boolean(id),
  });
}

export function useTeacherStatsQuery(id?: string) {
  return useQuery({
    queryKey: sandDollarsKeys.teacherStats(id ?? ''),
    queryFn: () => sandDollarStatsApi.teacherStats(id as string),
    enabled: Boolean(id),
  });
}

export function useClassListQuery(window: { from?: string; to?: string } = {}) {
  return useQuery({
    queryKey: sandDollarsKeys.classList(window),
    queryFn: () => sandDollarStatsApi.classList(window),
  });
}
